import React, { useState } from 'react';

import {
  faUser,
  faPhone,
  faCalendar
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Paper,
  Table,
  Badge,
  Pagination,
  Group,
  Text,
  LoadingOverlay,
  Alert,
  Title
} from '@mantine/core';
import { Link } from 'react-router-dom';

import { type Visit } from './types';
import { FormatPhone } from '../../utils/format';
import { usePostJSON } from '../../utils/request';

interface VisitResponse {
  success: boolean;
  visits: Visit[];
  total: number;
  page: number;
  limit: number;
}

interface VisitListProps {
  title: string;
}

const VisitList: React.FC<VisitListProps> = ({ title }) => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const {
    data,
    error,
    loading: isLoading
  } = usePostJSON<VisitResponse>(
    '/api/visit/data',
    { page, limit },
    [page, limit]
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil((data?.total || 0) / limit);
  const visits = data?.visits || [];
  const total = data?.total || 0;

  if (error) {
    return (
      <Paper withBorder p="md" style={{ position: 'relative' }}>
        <Alert color="red" title="Error">
          {error.message}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper withBorder p="md" style={{ position: 'relative' }}>
      <LoadingOverlay visible={isLoading} />

      <Group justify="space-between" mb="md">
        <Title order={3}>{title}</Title>
        <Badge color="blue" variant="light">
          {total} {total === 1 ? 'visit' : 'visits'}
        </Badge>
      </Group>

      {visits.length === 0 ? (
        <Text c="dimmed" py="xl" ta="center">
          No visits found.
        </Text>
      ) : (
        <>
          <Table highlightOnHover striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w="15%">Start</Table.Th>
                <Table.Th w="15%">End</Table.Th>
                <Table.Th w="15%">Client</Table.Th>
                <Table.Th w="15%">Caregiver</Table.Th>
                <Table.Th w="10%">Status</Table.Th>
                <Table.Th w="20%">Reminders</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {visits.map((visit) => (
                <Table.Tr key={visit.id}>
                  <Table.Td>
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faCalendar} size="sm" />
                      <Text size="sm">
                        {formatDate(visit.start)}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faCalendar} size="sm" />
                      <Text size="sm">
                        {formatDate(visit.end)}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faUser} size="sm" />
                      <Text
                        c="blue"
                        component={Link}
                        fw={500}
                        size="sm"
                        style={{ textDecoration: 'none' }}
                        to={`/client/${visit._client.id}/encounters`}
                      >
                        {visit._client.firstName} {visit._client.lastName}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <FontAwesomeIcon icon={faPhone} size="sm" />
                      <Text
                        c="blue"
                        component={Link}
                        size="sm"
                        style={{ textDecoration: 'none' }}
                        to={`/caller/edit/${visit._caller.id}`}
                      >
                        {visit._caller.firstName || visit._caller.lastName
                          ? `${visit._caller.firstName || ''} ${visit._caller.lastName || ''}`.trim()
                          : FormatPhone(visit._caller.phone)}
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    {visit.isVoided ? (
                      <Badge color="red" variant="light">
                        Voided
                      </Badge>
                    ) : (
                      <Badge color="green" variant="light">
                        Active
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {visit.reminderJobs?.weeklyCheckInId && (
                        <Badge color="blue" variant="light">
                          Weekly Check-In
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          {totalPages > 1 && (
            <Group justify="center" mt="md">
              <Pagination
                total={totalPages}
                value={page}
                onChange={setPage}
              />
            </Group>
          )}
        </>
      )}
    </Paper>
  );
};

export default VisitList;

